import { useEffect, useMemo, useState } from 'react';
import { sheetUrl, searchUrl } from './config';
import { useTheme } from './theme';

const DEVICE_KEY = 'wc_device_id';

// Stable device fingerprint — generated once per browser profile, kept in localStorage.
function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      // uuid v4-ish; good enough for vote audit trails
      id =
        'dev_' +
        ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
          (
            c ^
            (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
          ).toString(16)
        );
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return 'dev_unknown';
  }
}

// ISO-like timestamp with local offset
function timestampLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const offMin = -d.getTimezoneOffset();
  const sign = offMin >= 0 ? '+' : '-';
  const abs = Math.abs(offMin);
  const offH = pad(Math.floor(abs / 60));
  const offM = pad(abs % 60);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${offH}:${offM}`
  );
}

function makeVoteId() {
  return `v_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

const normalizeAudience = (a) => (a || '').toString().trim().toLowerCase();
const audienceLabel = (a) => {
  const x = normalizeAudience(a);
  if (x === 'board' || x === 'board only' || x === 'board-only') return 'Board Only';
  if (x === 'members' || x === 'members only' || x === 'members-only') return 'Members Only';
  return 'Everyone';
};

const stylesFor = (theme) => {
  const dark = theme === 'dark';
  return {
    page: {
      padding: 16,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 10,
      maxWidth: 900,
    },
    kicker: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    h2: {
      margin: 0,
      fontSize: 24,
      color: dark ? '#f8fafc' : '#0f172a',
    },
    headerActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    btn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#111827' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      cursor: 'pointer',
      fontSize: 14,
    },
    card: {
      border: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      borderRadius: 14,
      padding: 14,
      marginBottom: 12,
      background: dark ? 'rgba(17, 24, 39, 0.7)' : '#ffffff',
      boxShadow: dark
        ? '0 10px 24px rgba(0,0,0,0.25)'
        : '0 10px 24px rgba(15, 23, 42, 0.08)',
      maxWidth: 900,
    },
    label: {
      fontSize: 12,
      color: dark ? '#cbd5e1' : '#475569',
      fontWeight: 700,
      marginBottom: 6,
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
      maxWidth: 520,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
      maxWidth: 520,
    },
    muted: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      marginTop: 6,
    },
    grid2: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 10,
      marginTop: 10,
    },
    yesBtn: {
      padding: '14px 12px',
      borderRadius: 14,
      border: '1px solid #16a34a',
      background: '#16a34a',
      color: 'white',
      fontWeight: 900,
      fontSize: 16,
      cursor: 'pointer',
    },
    noBtn: {
      padding: '14px 12px',
      borderRadius: 14,
      border: '1px solid #dc2626',
      background: '#dc2626',
      color: 'white',
      fontWeight: 900,
      fontSize: 16,
      cursor: 'pointer',
    },
    disabled: { opacity: 0.55, cursor: 'not-allowed' },
    confirmBox: {
      marginTop: 12,
      padding: 12,
      borderRadius: 12,
      border: `1px solid ${dark ? 'rgba(251, 191, 36, 0.35)' : '#ecdca3'}`,
      background: dark ? 'rgba(251, 191, 36, 0.08)' : '#fff8d6',
      color: dark ? '#fef3c7' : '#3a2f00',
    },
  };
};

export default function VotePage({ authenticatedUser }) {
  const memberNumber = authenticatedUser?.memberNumber?.toString() || '';
  const memberName = authenticatedUser?.name || '';

  const isBoard = !!authenticatedUser?.isBoard;
  const isMember = true; // anyone logged in is effectively "member" for voting purposes

  const [theme] = useTheme();
  const S = stylesFor(theme);

  const [ballots, setBallots] = useState([]);
  const [ballotName, setBallotName] = useState('');
  const [loadingBallots, setLoadingBallots] = useState(true);

  const [myVotes, setMyVotes] = useState([]);
  const [loadingVotes, setLoadingVotes] = useState(true);
  const [status, setStatus] = useState('');

  // Confirmation flow state
  const [pendingChoice, setPendingChoice] = useState(null); // 'Yes' | 'No' | null

  // Load Ballots from sheet.
  //
  // Audience filtering: Board-only ballots are dropped from the list entirely
  // for non-board, non-admin users. They never appear in the dropdown and the
  // member is never told they exist — that's the intent (no "you don't have
  // access" message).
  const fetchBallots = async () => {
    setLoadingBallots(true);
    try {
      const res = await fetch(sheetUrl('Ballots'), { cache: 'no-store' });
      const data = await res.json();
      const canSeeBoardOnly = isBoard || !!authenticatedUser?.isAdmin;
      const list = (data || [])
        .filter((r) => (r.name || '').toString().trim() !== '')
        .map((r) => ({
          name: (r.name || '').toString().trim(),
          display: (r.display || '').toString().trim(),
          audience: audienceLabel(r.audience || ''),
          maxVotes: r.maxVotes ? Number(r.maxVotes) : 2,
        }))
        .filter((b) => b.audience !== 'Board Only' || canSeeBoardOnly)
        .sort((a, b) => a.name.localeCompare(b.name));

      setBallots(list);
      if (!ballotName && list.length) setBallotName(list[0].name);
    } catch (e) {
      console.error('VotePage: ballots load failed', e);
      setBallots([]);
    } finally {
      setLoadingBallots(false);
    }
  };

  const fetchMyVotes = async (memberNum, typeName) => {
    if (!memberNum || !typeName) return;
    setLoadingVotes(true);
    setStatus('');
    try {
      const res = await fetch(searchUrl('Votes', { memberNumber: memberNum }), {
        cache: 'no-store',
      });
      const data = await res.json();
      const filtered = (data || []).filter((r) => (r.type || '') === typeName);
      setMyVotes(filtered);
    } catch (e) {
      console.error('VotePage: fetch votes failed', e);
      setMyVotes([]);
      setStatus('Failed to load votes.');
    } finally {
      setLoadingVotes(false);
    }
  };

  useEffect(() => {
    fetchBallots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (memberNumber && ballotName) fetchMyVotes(memberNumber, ballotName);
    setPendingChoice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberNumber, ballotName]);

  const selectedBallot = useMemo(
    () => ballots.find((b) => b.name === ballotName) || null,
    [ballots, ballotName]
  );

  const canSeeBallot = useMemo(() => {
    if (!selectedBallot) return false;
    if (selectedBallot.audience === 'Everyone') return true;
    if (selectedBallot.audience === 'Members Only') return isMember;
    if (selectedBallot.audience === 'Board Only') return isBoard || authenticatedUser?.isAdmin;
    return true;
  }, [selectedBallot, isBoard, isMember, authenticatedUser]);

  const maxVotes = selectedBallot?.maxVotes || 2;
  const usedVotes = myVotes.length;
  const remainingVotes = Math.max(0, maxVotes - usedVotes);

  const breakdown = useMemo(() => {
    let yes = 0;
    let no = 0;
    myVotes.forEach((v) => {
      const c = (v.choice || '').toLowerCase();
      if (c === 'yes') yes += 1;
      if (c === 'no') no += 1;
    });
    return { yes, no };
  }, [myVotes]);

  const submitVote = async () => {
    if (!pendingChoice) return;
    if (!memberNumber) return alert('Not logged in.');
    if (!selectedBallot) return alert('No ballot selected.');
    if (!canSeeBallot) return alert('You do not have access to vote on this ballot.');
    if (remainingVotes <= 0) return alert(`You already used your ${maxVotes} votes.`);

    setStatus('Submitting…');

    const row = {
      vote_id: makeVoteId(),
      memberNumber,
      memberName,
      voteDate: new Date().toISOString().slice(0, 10),
      type: selectedBallot.name,
      choice: pendingChoice,
      timestamp: timestampLocal(),
      deviceId: getDeviceId(),
      userAgent: (navigator.userAgent || '').slice(0, 250),
    };

    try {
      const res = await fetch(sheetUrl('Votes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: [row] }),
      });
      if (!res.ok) throw new Error(await res.text());

      setStatus('Vote recorded.');
      setPendingChoice(null);
      await fetchMyVotes(memberNumber, selectedBallot.name);
    } catch (e) {
      console.error('VotePage: submit failed', e);
      setStatus('Failed to submit vote.');
      alert('Failed to submit vote.');
    }
  };

  const startVote = (choice) => {
    if (remainingVotes <= 0) return alert(`You already used your ${maxVotes} votes.`);
    setPendingChoice(choice);
    setStatus('');
  };

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <div style={S.kicker}>Voting</div>
          <h2 style={S.h2}>🗳️ Ballots</h2>
          <div style={S.muted}>
            You get a limited number of votes per ballot.
          </div>
        </div>

        <div style={S.headerActions}>
          <button style={S.btn} onClick={fetchBallots}>
            Refresh ballots
          </button>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.label}>Select ballot</div>
        <select
          value={ballotName}
          onChange={(e) => setBallotName(e.target.value)}
          style={S.select}
          disabled={loadingBallots || ballots.length === 0}
        >
          {ballots.length === 0 ? (
            <option value="">No ballots found</option>
          ) : (
            ballots.map((b) => (
              <option key={b.name} value={b.name}>
                {b.name} — {b.display || '(no display text)'}
              </option>
            ))
          )}
        </select>

        {loadingBallots ? (
          <div style={{ marginTop: 10 }}>Loading ballots…</div>
        ) : !selectedBallot ? (
          <div style={{ marginTop: 10 }}>No ballot selected.</div>
        ) : !canSeeBallot ? (
          // Defensive: with the fetch-time filter, this branch shouldn't be
          // reachable for non-board users. Kept as a safety net for stale
          // dropdowns; phrased neutrally so it doesn't leak the audience.
          <div style={{ marginTop: 10, ...S.muted }}>
            This ballot isn't available right now.
          </div>
        ) : (
          <>
            <div style={{ marginTop: 12, fontWeight: 900, fontSize: 16 }}>
              {selectedBallot.display || selectedBallot.name}
            </div>

            <div style={S.muted}>
              Ballot type: <b>{selectedBallot.name}</b> • Audience:{' '}
              <b>{selectedBallot.audience}</b> • Max votes: <b>{maxVotes}</b>
            </div>

            <div style={{ marginTop: 10 }}>
              Remaining votes:{' '}
              <b style={{ fontSize: 18, color: remainingVotes > 0 ? '#16a34a' : '#dc2626' }}>
                {remainingVotes}
              </b>
              <div style={S.muted}>
                You voted: Yes {breakdown.yes} / No {breakdown.no}
              </div>
            </div>

            {!pendingChoice ? (
              <div style={S.grid2}>
                <button
                  onClick={() => startVote('Yes')}
                  disabled={remainingVotes <= 0 || loadingVotes}
                  style={{
                    ...S.yesBtn,
                    ...(remainingVotes <= 0 || loadingVotes ? S.disabled : {}),
                  }}
                >
                  ✅ YES
                </button>

                <button
                  onClick={() => startVote('No')}
                  disabled={remainingVotes <= 0 || loadingVotes}
                  style={{
                    ...S.noBtn,
                    ...(remainingVotes <= 0 || loadingVotes ? S.disabled : {}),
                  }}
                >
                  ❌ NO
                </button>
              </div>
            ) : (
              <div style={S.confirmBox}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>
                  Confirm your <span style={{ textDecoration: 'underline' }}>{pendingChoice.toUpperCase()}</span> vote
                </div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  Once submitted this vote can't be changed.
                </div>
                <div style={{ ...S.grid2, marginTop: 10 }}>
                  <button
                    onClick={submitVote}
                    disabled={loadingVotes}
                    style={{
                      ...S.yesBtn,
                      ...(loadingVotes ? S.disabled : {}),
                    }}
                    autoFocus
                  >
                    Confirm {pendingChoice}
                  </button>
                  <button
                    onClick={() => setPendingChoice(null)}
                    style={S.btn}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={S.muted}>{loadingVotes ? 'Loading votes…' : status}</div>
          </>
        )}
      </div>
    </div>
  );
}
